import { useTranslation } from "react-i18next";
import { formatLabel } from "@/lib/formatLabels";
import { useL } from "@/lib/useL";
import React, { useEffect, useRef, useState } from "react";
import { ComplementRemovers } from "@/components/ComplementRemovers";
import { PrintQr } from "@/components/PrintQr";
import { shareUrlFor } from "@/lib/shareText";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShareAndPrint } from "@/components/ShareAndPrint";
import { SpeakButton } from "@/components/SpeakButton";
import { DETAIL } from "@/constants/testIds";
import {
  ChevronLeft,
  Copy,
  Trash2,
  ChevronDown,
  ChevronUp,
  Lock,
  Link as LinkIcon,
  WifiOff,
  RefreshCw,
  Maximize2,
} from "lucide-react";
import { renderSummary } from "@/lib/renderSummary";
import { ReciteBox } from "@/components/ReciteBox";
import { VerifiedSummary } from "@/components/VerifiedSummary";
import { FlashcardsButton } from "@/components/FlashcardsButton";
import { MindmapButton } from "@/components/MindmapButton";
import { cacheDetail, loadCachedDetail, forgetDetail } from "@/lib/summariesCache";

export default function SummaryDetail() {
  const { t, i18n } = useTranslation();
  const L = useL();
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const articleRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [showOriginal, setShowOriginal] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [fromCache, setFromCache] = useState(false);
  const [expanding, setExpanding] = useState(false);

  const nextLength = (cur) => {
    if (cur === "short") return "medium";
    if (cur === "medium") return "long";
    return null; // long already → no next step
  };

  const onRework = () => {
    if (!item) return;
    // Hand the source text + settings to the Dashboard via router state.
    // Dashboard reads location.state.rework on mount, prefills, and clears.
    navigate("/", {
      state: {
        rework: {
          text: item.original_text || item.summary,
          model: item.model,
          length: item.length,
          format: item.format,
          language: item.language,
          register: item.register || "auto",
          sourceTitle: item.title,
        },
      },
    });
  };

  const onExpand = async () => {
    if (!item || expanding) return;
    const next = nextLength(item.length);
    if (!next) {
      toast.info(L("Ce résumé est déjà à sa longueur maximale.", "This summary is already at its maximum length."));
      return;
    }
    setExpanding(true);
    try {
      const { data } = await api.post("/summarize", {
        text: item.summary,
        model: item.model,
        length: next,
        format: item.format,
        language: item.language,
        register: item.register || "auto",
        source_type: "text",
        source_label: `${L("Développement de :", "Expansion of:")} ${item.title}`,
      });
      cacheDetail(data);
      toast.success(L("Résumé développé !", "Summary expanded!"));
      navigate(`/history/${data.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || L("Impossible de développer ce résumé.", "Could not expand this summary.");
      toast.error(detail);
    } finally {
      setExpanding(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFromCache(false);
    api
      .get(`/summaries/${id}`)
      .then(({ data }) => {
        if (cancelled) return;
        setItem(data);
        cacheDetail(data);
      })
      .catch(async () => {
        if (cancelled) return;
        // Network / auth error → fall back to the offline cache.
        const cached = await loadCachedDetail(id);
        if (cached) {
          setItem(cached.summary);
          setFromCache(true);
        } else {
          toast.error(t("detail.not_found", "Résumé introuvable."));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const onCopy = async () => {
    if (!item?.summary) return;
    try {
      await navigator.clipboard.writeText(item.summary);
      toast.success(t("detail.copied", "Copié dans le presse-papiers."));
    } catch {
      toast.error(L("Impossible de copier : vérifiez les permissions du navigateur.", "Could not copy: check your browser permissions."));
    }
  };

  const onDelete = async () => {
    if (!window.confirm(t("detail.confirm_delete", "Supprimer définitivement ce résumé ?"))) return;
    try {
      await api.delete(`/summaries/${id}`);
      forgetDetail(id);
      toast.success(t("detail.deleted", "Résumé supprimé."));
      navigate("/history");
    } catch {
      toast.error(t("detail.delete_failed", "Suppression impossible."));
    }
  };

  const onToggleShare = async () => {
    if (!item) return;
    setSharing(true);
    try {
      const { data } = await api.post(`/summaries/${id}/share`);
      setItem((cur) => ({ ...cur, is_public: data.is_public }));
      toast.success(
        data.is_public
          ? L("Lien public activé. N'importe qui peut désormais lire ce résumé via le lien.", "Public link on. Anyone with the link can now read this summary.")
          : L("Lien public désactivé. Vous seul pouvez voir ce résumé.", "Public link off. Only you can see this summary.")
      );
    } catch {
      toast.error(t("detail.share_failed", "Impossible de modifier le partage."));
    } finally {
      setSharing(false);
    }
  };

  const shareUrl = item ? shareUrlFor(item.id) : "";

  const onCopyShareLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("detail.link_copied", "Lien de partage copié."));
    } catch {
      toast.error(L("Impossible de copier le lien : vérifiez les permissions du navigateur.", "Could not copy the link: check your browser permissions."));
    }
  };

  if (loading) return <div className="p-12 sux-marker">Loading…</div>;
  if (!item) return <div className="p-12 sux-marker">Not found.</div>;

  const date = new Date(item.created_at);
  // For very short inputs (< 100 chars, e.g. a greeting), the summary can be
  // longer than the source when the model expands it — showing a negative
  // percentage is confusing. Show a friendly label instead.
  const rawRatio =
    (1 - item.summary_chars / Math.max(item.original_chars, 1)) * 100;
  const ratio = Math.round(rawRatio);
  const compressionLabel =
    item.original_chars < 100 || ratio < 0 ? "N/A" : `-${ratio}%`;

  return (
    <div className="border-t border-border">
      {fromCache && (
        <div
          data-testid="summary-cache-banner"
          className="px-6 md:px-10 py-2 border-b border-border bg-[#002FA7]/5 flex items-center gap-2 font-mono uppercase tracking-[0.15em] text-[10px] text-[#002FA7]"
        >
          <WifiOff className="w-3 h-3" /> {L("Copie hors ligne : dernière mise à jour lors de votre dernière visite.", "Offline copy: last updated on your previous visit.")}
        </div>
      )}
      <div className="px-6 md:px-10 py-6 border-b border-border flex items-center justify-between flex-wrap gap-3">
        <Link
          to="/history"
          data-testid={DETAIL.back}
          className="font-mono uppercase tracking-[0.2em] text-[11px] flex items-center gap-2 hover:text-[#002FA7]"
        >
          <ChevronLeft className="w-3 h-3" /> {t("detail.back", "Retour à l'historique")}
        </Link>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={onRework}
            data-testid="detail-rework-btn"
            className="font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-2 bg-[#002FA7] text-white border border-[#002FA7] hover:bg-[#00227A] transition-colors flex items-center gap-2"
            title={L("Repartir du texte source avec d'autres réglages", "Start again from the source text with other settings")}
          >
            <RefreshCw className="w-3.5 h-3.5" /> {L("Retravailler", "Rework")}
          </button>
          <button
            onClick={onExpand}
            disabled={expanding || item.length === "long"}
            data-testid="detail-expand-btn"
            className="font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-2 border border-border hover:bg-[#FF2A00] hover:text-white hover:border-[#002FA7] transition-colors flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            title={
              item.length === "long"
                ? L("Ce résumé est déjà à sa longueur maximale", "This summary is already at its maximum length")
                : L("Générer une version plus détaillée de ce résumé", "Generate a more detailed version of this summary")
            }
          >
            <Maximize2 className="w-3.5 h-3.5" /> {expanding ? "…" : L("Développer", "Expand")}
          </button>
          <button
            onClick={onCopy}
            data-testid={DETAIL.copy}
            className="font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-2 border border-border hover:bg-[#FF2A00] hover:text-white hover:border-[#002FA7] transition-colors flex items-center gap-2"
          >
            <Copy className="w-3.5 h-3.5" /> {t("detail.copy", "Copier")}
          </button>
          <SpeakButton text={item.summary} lang={item.language} testId="detail-speak-btn" />
          <ShareAndPrint
            title={item.title}
            text={item.summary}
            isPublic={item.is_public}
            publicUrl={shareUrl}
            onEnsurePublic={onToggleShare}
            sharing={sharing}
            testidPrefix="detail"
          />
          <button
            onClick={onDelete}
            data-testid={DETAIL.delete}
            className="font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-2 border border-border hover:bg-[#FF2A00] hover:text-white hover:border-[#FF2A00] transition-colors flex items-center gap-2"
          >
            <Trash2 className="w-3.5 h-3.5" /> {t("detail.delete", "Supprimer")}
          </button>
        </div>
      </div>

      {item.is_public && (
        <div className="px-6 md:px-10 py-4 bg-[#002FA7]/5 border-b border-[#002FA7]/20">
          <div className="max-w-3xl mx-auto flex items-center gap-3 flex-wrap">
            <LinkIcon className="w-4 h-4 text-[#002FA7] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="sux-marker mb-1">{t("detail.public_link", "Lien de partage public")}</div>
              <a
                href={shareUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={DETAIL.shareLink}
                className="font-mono text-xs text-[#002FA7] hover:underline truncate block"
              >
                {shareUrl}
              </a>
            </div>
            <button
              onClick={onCopyShareLink}
              data-testid={DETAIL.copyShareLink}
              className="font-mono uppercase tracking-[0.14em] text-[11px] px-3 py-2 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors flex items-center gap-2"
            >
              <Copy className="w-3.5 h-3.5" /> {t("detail.copy_link", "Copier le lien")}
            </button>
            <button
              onClick={onToggleShare}
              disabled={sharing}
              data-testid={DETAIL.share}
              className="font-mono uppercase tracking-[0.14em] text-[11px] px-3 py-2 border border-border text-muted-foreground hover:bg-[#FF2A00] hover:text-white hover:border-[#FF2A00] transition-colors disabled:opacity-50 flex items-center gap-2"
              title={L("Rendre ce résumé de nouveau privé (le lien public ne fonctionnera plus)", "Make this summary private again (the public link will stop working)")}
            >
              <Lock className="w-3.5 h-3.5" /> {L("Rendre privé", "Make private")}
            </button>
          </div>
        </div>
      )}

      <article className="max-w-3xl mx-auto px-6 md:px-8 py-12 md:py-16">
        <div className="sux-marker mb-4">
          {date.toLocaleString(i18n.language, {
            dateStyle: "long",
            timeStyle: "short",
          })}
        </div>
        <h1
          className="text-4xl md:text-5xl font-black tracking-tight mb-8 leading-tight"
          style={{ fontFamily: "Outfit" }}
        >
          {item.title}
        </h1>

        <div className="grid grid-cols-2 md:grid-cols-4 border border-border mb-10">
          {[
            [t("detail.model", "Modèle"), item.model],
            [t("detail.length", "Longueur"), t(`detail.len_${item.length}`, item.length)],
            [t("detail.format", "Format"), formatLabel(item.format, L(false, true))],
            [t("detail.compression", "Compression"), compressionLabel],
          ].map(([k, v]) => (
            <div
              key={k}
              className="p-4 border-r border-b md:border-b-0 border-border last:border-r-0"
            >
              <div className="sux-marker mb-1">{k}</div>
              <div className="font-mono text-sm">{v}</div>
            </div>
          ))}
        </div>

        <VerifiedSummary
          articleRef={articleRef}
          className="summary-content notranslate"
          summary={item.summary}
          source={item.original_text}
          format={item.format}
          testid="detail-summary"
        />
        {item.format === "poeme" && <div className="mt-8"><ReciteBox summary={item.summary} lang={item.language} /></div>}
        <ComplementRemovers
          articleRef={articleRef}
          summaryId={item.id}
          summary={item.summary}
          title={item.title}
          onChange={(s) => setItem((cur) => ({ ...cur, summary: s, summary_chars: s.length }))}
        />
        <div className="mt-6"><FlashcardsButton summaryId={item.id} /></div>
        <div className="max-w-3xl"><MindmapButton summaryId={item.id} /></div>
        <PrintQr url={item.is_public ? shareUrl : null} />

        <div className="mt-12 border-t border-border pt-6">
          <button
            onClick={() => setShowOriginal((v) => !v)}
            data-testid={DETAIL.toggleOriginal}
            className="font-mono uppercase tracking-[0.2em] text-[11px] flex items-center gap-2 text-muted-foreground hover:text-[#002FA7]"
          >
            {showOriginal ? (
              <ChevronUp className="w-3 h-3" />
            ) : (
              <ChevronDown className="w-3 h-3" />
            )}
            {showOriginal ? t("detail.hide_original", "Masquer le texte d'origine") : t("detail.show_original", "Afficher le texte d'origine")} (
            {item.original_chars.toLocaleString(i18n.language)} {t("detail.chars", "caractères")})
          </button>
          {showOriginal && (
            <div className="mt-4 border border-border p-6 bg-secondary/30 text-sm whitespace-pre-wrap leading-relaxed max-h-[60vh] overflow-auto">
              {item.original_text}
            </div>
          )}
        </div>
      </article>
    </div>
  );
}
